import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import BrandLogo from "../shared/BrandLogo";

const Navbar = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-150 ${
        isScrolled ? "bg-white shadow-md border-b border-slate-200/50" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <BrandLogo className="group" logoClassName="h-9" />

          <div className="hidden lg:flex items-center gap-8">
            {[
              { label: t("nav.howItWorks"), id: "how-it-works" },
              { label: t("nav.pricing"), id: "benefits" },
              { label: t("nav.corporate"), id: "faq" },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className={`text-sm font-semibold transition-colors hover:text-red-600 ${
                  isScrolled ? "text-slate-700" : "text-slate-700"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={() => navigate("/admin")}
              className={`text-sm font-semibold px-4 py-2 rounded-lg transition-colors duration-150 hover:bg-slate-100 ${
                isScrolled ? "text-slate-700" : "text-slate-700"
              }`}
            >
              {t("nav.login")}
            </button>
            <button
              onClick={() => scrollTo("cta")}
              className="text-sm font-bold px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-150 shadow-md shadow-red-600/25"
            >
              {t("nav.getStarted")}
            </button>
          </div>

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            {menuOpen ? <X size={24} className="text-slate-800" /> : <Menu size={24} className="text-slate-800" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-200 shadow-md animate-fade-in">
          <div className="px-4 py-4 space-y-1">
            {[
              { label: t("nav.howItWorks"), id: "how-it-works" },
              { label: t("nav.pricing"), id: "benefits" },
              { label: t("nav.corporate"), id: "faq" },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="block w-full text-left px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
              >
                {item.label}
              </button>
            ))}
            <hr className="my-2 border-slate-200" />
            <button
              onClick={() => {
                setMenuOpen(false);
                navigate("/admin");
              }}
              className="block w-full text-left px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-lg"
            >
              {t("nav.login")}
            </button>
            <button
              onClick={() => scrollTo("cta")}
              className="block w-full text-center px-4 py-3 text-sm font-bold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors mt-2"
            >
              {t("nav.getStarted")}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
